import { useState } from "react";
import SunAccent from "@/components/feature/SunAccent";
import Reveal from "@/components/base/Reveal";

export interface FaqItem {
  q: string;
  a: string;
}

/**
 * Builds a FAQPage node from the questions that are actually rendered.
 * Returns null for an empty list, because a FAQPage with no `mainEntity`
 * is invalid structured data and gets flagged by Google.
 */
export const buildFaqPageSchema = (faqs: FaqItem[]) => {
  if (!faqs || faqs.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.a,
      },
    })),
  };
};

interface FaqAccordionProps {
  faqs: FaqItem[];
  eyebrow?: string;
  title?: string;
  intro?: string;
  id?: string;
  className?: string;
}

export default function FaqAccordion({
  faqs,
  eyebrow = "Good to Know",
  title = "Frequently Asked Questions",
  intro,
  id = "faq",
  className = "bg-background-50",
}: FaqAccordionProps) {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section id={id} className={`${className} py-20 md:py-24`}>
      <div className="w-full px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12">
          <Reveal className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <SunAccent circleClassName="w-8 h-8" iconClassName="text-base" /><span className="h-px w-10 bg-accent-500"></span>
              <span className="text-xs font-semibold tracking-[0.24em] uppercase text-accent-600">
                {eyebrow}
              </span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl leading-tight text-foreground-950 font-semibold">
              {title}
            </h2>
            {intro ? (
              <p className="mt-5 text-base md:text-lg text-foreground-700 leading-relaxed">
                {intro}
              </p>
            ) : null}
          </Reveal>

          <Reveal className="lg:col-span-7" delay={120}>
            <div className="space-y-3">
              {faqs.map((faq, index) => {
                const isOpen = open === index;
                return (
                  <div
                    key={faq.q}
                    className={`bg-background-100 rounded-lg border transition-colors ${
                      isOpen ? "border-accent-300" : "border-background-200"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setOpen(isOpen ? null : index)}
                      className="w-full flex items-center justify-between gap-4 text-left px-6 py-5 cursor-pointer"
                      aria-expanded={isOpen}
                    >
                      <span className="font-heading text-base md:text-lg text-foreground-950 font-semibold">
                        {faq.q}
                      </span>
                      <span
                        className={`w-8 h-8 flex items-center justify-center rounded-full shrink-0 transition-colors ${
                          isOpen
                            ? "bg-accent-500 text-background-50"
                            : "bg-background-200 text-foreground-700"
                        }`}
                      >
                        <i
                          className={`${isOpen ? "ri-subtract-line" : "ri-add-line"} text-lg`}
                        ></i>
                      </span>
                    </button>
                    {isOpen && (
                      <p className="px-6 pb-6 text-sm md:text-base text-foreground-600 leading-relaxed">
                        {faq.a}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}